import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with credentials for httpOnly cookies
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send httpOnly cookies with every request
});

// Add auth token to requests (fallback for legacy support)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/apply')) {
        window.location.href = '/org/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => 
  api.post('/auth/login', { email, password });

export const getMe = () => 
  api.get('/auth/me');

export const resetPassword = (currentPassword, newPassword) =>
  api.post('/auth/reset-password', { current_password: currentPassword, new_password: newPassword });

// Logout (clears httpOnly cookie)
export const logout = () =>
  api.post('/auth/logout');

// Application Flow (Public)
export const getPublicStandards = () =>
  api.get('/public/standards');

export const getCertificationPricing = () =>
  api.get('/public/certification-pricing');

export const submitApplication = (data) =>
  api.post('/apply', data);

// My Application (Org User)
export const getMyApplication = () =>
  api.get('/applications/my-application');

// Payment
export const createCheckoutSession = () =>
  api.post('/payments/create-checkout');

export const getPaymentStatus = (sessionId) =>
  api.get(`/payments/status/${sessionId}`);

export const createRenewalCheckout = () =>
  api.post('/payments/renewal-checkout');

export const getRenewalInfo = () =>
  api.get('/payments/renewal-info');

// Organizations
export const getOrganizations = () => 
  api.get('/organizations');

export const getOrganization = (id) => 
  api.get(`/organizations/${id}`);

export const createOrganization = (data) => 
  api.post('/organizations', data);

export const updateOrganization = (id, data) => 
  api.put(`/organizations/${id}`, data);

export const toggleFoundingMember = (id, enable) =>
  api.put(`/organizations/${id}/founding-member`, null, { params: { enable } });

// Application Management (Admin)
export const getPendingApplications = () =>
  api.get('/applications/pending');

export const getAllApplications = () =>
  api.get('/applications/all');

export const approveApplication = (orgId, notes = null) =>
  api.post(`/applications/${orgId}/approve`, notes ? { notes } : {});

export const denyApplication = (orgId, reason) =>
  api.post(`/applications/${orgId}/deny`, { reason });

// Users
export const getUsers = (orgId = null) => 
  api.get('/users', { params: { org_id: orgId } });

export const createUser = (data) => 
  api.post('/users', data);

export const deleteUser = (id) => 
  api.delete(`/users/${id}`);

// Standards
export const getStandards = () => 
  api.get('/standards');

export const getStandard = (id) => 
  api.get(`/standards/${id}`);

export const updateStandard = (id, data) => 
  api.put(`/standards/${id}`, data);

// Certifications
export const getCertifications = (orgId = null, status = null) => 
  api.get('/certifications', { params: { org_id: orgId, status } });

export const getExpiringCertifications = () =>
  api.get('/certifications/expiring');

export const getCertification = (id) => 
  api.get(`/certifications/${id}`);

export const createCertification = (data) => 
  api.post('/certifications', data);

export const updateCertification = (id, data) => 
  api.put(`/certifications/${id}`, data);

export const renewCertification = (id) => 
  api.post(`/certifications/${id}/renew`);

export const revokeCertification = (id, reason) => 
  api.post(`/certifications/${id}/revoke`, { reason });

export const downloadCertificatePdf = async (id) => {
  const response = await api.get(`/certifications/${id}/pdf`, {
    responseType: 'blob',
  });
  return response;
};

// Tools
export const getTools = (status = null) => 
  api.get('/tools', { params: { status } });

export const getTool = (id) => 
  api.get(`/tools/${id}`);

export const updateTool = (id, data) => 
  api.put(`/tools/${id}`, data);

// Reports
export const getReports = () => 
  api.get('/reports');

export const getReport = (id) => 
  api.get(`/reports/${id}`);

export const createReport = (data) => 
  api.post('/reports', data);

export const downloadReportPdf = async (id) => {
  const response = await api.get(`/reports/${id}/pdf`, {
    responseType: 'blob',
  });
  return response;
};

export const toggleReportShare = (id, enable) => 
  api.put(`/reports/${id}/share`, null, { params: { enable } });

// Notifications
export const getNotifications = () =>
  api.get('/notifications');

export const markNotificationRead = (id) =>
  api.put(`/notifications/${id}/read`);

// Audit Logs
export const getAuditLogs = (limit = 100, actionType = null) =>
  api.get('/audit-logs', { params: { limit, action_type: actionType } });

// Admin Export
export const exportRegistryCSV = async () => {
  const response = await api.get('/admin/registry/export-csv', {
    responseType: 'blob',
  });
  return response;
};

// Public endpoints
export const getRegistry = (page = 1, limit = 20) => 
  api.get('/public/registry', { params: { page, limit } });

export const getRegistryCount = () => 
  api.get('/public/registry/count');

export const verifyCertification = (certificationId) => 
  api.get(`/public/verify/${certificationId}`);

export const getPublicReport = (reportId) => 
  api.get(`/public/reports/${reportId}`);

export const getPricing = () =>
  api.get('/public/pricing');

export const downloadPublicReportPdf = async (reportId) => {
  const response = await api.get(`/public/reports/${reportId}/pdf`, {
    responseType: 'blob',
  });
  return response;
};

// Health
export const healthCheck = () => 
  api.get('/health');

// ============ AI RISK ASSESSMENT FUNNEL ============

export const submitAssessment = (data) =>
  api.post('/assessment/submit', data);

export const getAssessment = (assessmentId) =>
  api.get(`/assessment/${assessmentId}`);

export const getAssessmentByEmail = (email) =>
  api.get(`/assessment/by-email/${email}`);

export const createAssessmentCheckout = (assessmentId) =>
  api.post(`/assessment/${assessmentId}/create-checkout`);

export const verifyAssessmentPurchase = (assessmentId, sessionId) =>
  api.get(`/assessment/${assessmentId}/verify-purchase`, { params: { session_id: sessionId } });

export const getAssessmentDownload = (assessmentId) =>
  api.get(`/assessment/${assessmentId}/download`);

// Admin Assessment Endpoints
export const getAdminAssessments = (page = 1, limit = 50, status = null, purchased = null) =>
  api.get('/admin/assessments', { params: { page, limit, status, purchased } });

export const getAssessmentStats = () =>
  api.get('/admin/assessments/stats');

export const exportAssessmentsCSV = async () => {
  const response = await api.get('/admin/assessments/export-csv', {
    responseType: 'blob',
  });
  return response;
};

// Axiom Analytics (Admin)
export const getAxiomAnalytics = () =>
  api.get('/admin/axiom/analytics');

// Axiom PDF Export
export const exportAxiomResponsePdf = async (content, title) => {
  const response = await api.post('/axiom/export-pdf', { content, title }, {
    responseType: 'blob',
  });
  return response;
};

export const exportAxiomConversationPdf = async (sessionId) => {
  const response = await api.get(`/axiom/conversation/${sessionId}/export-pdf`, {
    responseType: 'blob',
  });
  return response;
};


export default api;
